// frontend/src/pages/About.js — full "À propos" story page, matching the
// landing aesthetic (dark + acid-lime, Barlow Condensed / Manrope).
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Logo } from "../assets/logo.svg";

const AC = "#B4FF3A";
const BG = "#070707";

const About = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800;900&family=Manrope:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    window.scrollTo(0, 0);
    return () => {
      try { document.head.removeChild(link); } catch (_) {}
    };
  }, []);

  // Reusable bits ----------------------------------------------------------
  const Eyebrow = ({ children }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
      <div style={{ width: 7, height: 7, borderRadius: "50%", background: AC, boxShadow: `0 0 8px ${AC}` }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: AC, letterSpacing: ".16em", textTransform: "uppercase" }}>{children}</span>
    </div>
  );

  const H2 = ({ children }) => (
    <h2 className="bc" style={{ fontSize: "clamp(32px,4.6vw,58px)", fontWeight: 900, color: "#F2F2F2", textTransform: "uppercase", letterSpacing: "-1px", lineHeight: 1.0, marginBottom: 28 }}>{children}</h2>
  );

  const P = ({ children, muted }) => (
    <p style={{ fontSize: "clamp(16px,1.55vw,19px)", lineHeight: 1.85, color: muted ? "#6A6A6A" : "#9A9A9A", fontWeight: 400 }}>{children}</p>
  );

  const Strong = ({ children }) => <strong style={{ color: "#F2F2F2", fontWeight: 600 }}>{children}</strong>;

  const wrap = { maxWidth: 780, margin: "0 auto", padding: "0 32px" };
  const block = { display: "flex", flexDirection: "column", gap: 22 };
  const quote = { margin: "36px 0 0", paddingLeft: 26, borderLeft: `3px solid ${AC}` };
  const quoteText = { fontSize: "clamp(26px,3.6vw,42px)", fontWeight: 800, color: "#F2F2F2", lineHeight: 1.12, letterSpacing: "-.5px" };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", background: BG, color: "#F2F2F2", overflowX: "hidden" }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        .bc { font-family: 'Barlow Condensed', sans-serif; }
        .ba { background:${AC}; color:#000; border:none; cursor:pointer; font-family:'Manrope',sans-serif; font-weight:700; transition:all .22s; }
        .ba:hover { background:#ccff44; transform:translateY(-1px); }
        .bo { background:transparent; border:1px solid rgba(255,255,255,.14); color:#F2F2F2; cursor:pointer; font-family:'Manrope',sans-serif; font-weight:600; transition:all .22s; }
        .bo:hover { border-color:rgba(255,255,255,.4); background:rgba(255,255,255,.04); }
      `}</style>

      {/* NAV */}
      <nav style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(7,7,7,.9)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "16px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => navigate("/")}>
            <Logo style={{ height: 24 }} />
            <span className="bc" style={{ fontSize: 19, fontWeight: 900, letterSpacing: "1px", textTransform: "uppercase" }}>1PARC</span>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="bo" style={{ padding: "9px 18px", borderRadius: 8, fontSize: 13 }} onClick={() => navigate("/")}>← Accueil</button>
            <button className="ba" style={{ padding: "9px 18px", borderRadius: 8, fontSize: 13 }} onClick={() => navigate("/signup")}>Commencer →</button>
          </div>
        </div>
      </nav>

      {/* HERO — Mission */}
      <section style={{ padding: "100px 0 80px", borderBottom: "1px solid rgba(255,255,255,.06)", position: "relative" }}>
        <div style={{ position: "absolute", top: "-8%", left: "50%", transform: "translateX(-50%)", width: 680, height: 680, borderRadius: "50%", background: "radial-gradient(circle, rgba(180,255,58,.06) 0%, transparent 62%)", pointerEvents: "none" }} />
        <div style={{ ...wrap, position: "relative" }}>
          <Eyebrow>À propos de 1PARC</Eyebrow>
          <h1 className="bc" style={{ fontSize: "clamp(44px,6.5vw,86px)", fontWeight: 900, color: "#F2F2F2", textTransform: "uppercase", letterSpacing: "-2px", lineHeight: .95, marginBottom: 32 }}>
            Notre mission :<br /><span style={{ color: AC }}>la transparence.</span>
          </h1>
          <div style={block}>
            <P><Strong>Notre mission est de rendre chaque décision liée à l'entretien automobile transparente, traçable et équitable.</Strong></P>
            <P>Parce qu'un propriétaire de véhicule ne devrait jamais avoir à prendre une décision importante sans comprendre ce qui est réellement fait sur sa voiture.</P>
          </div>
        </div>
      </section>

      {/* THE QUESTION */}
      <section style={{ padding: "84px 0", background: "#040404", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={wrap}>
          <Eyebrow>Tout est parti d'une question</Eyebrow>
          <H2>Pourquoi l'entretien d'une voiture est-il encore une source de stress&nbsp;?</H2>
          <div style={block}>
            <P>Si vous possédez une voiture, vous avez probablement déjà vécu cette situation.</P>
            <P>Vous entrez dans un garage avec un problème précis. Quelques heures plus tard, on vous présente un devis rempli de termes techniques et de références de pièces que vous ne connaissez pas. On vous demande de payer des centaines de milliers de francs CFA sans réellement savoir ce que vous achetez.</P>
            <P>À ce moment-là, plusieurs questions se posent naturellement&nbsp;:</P>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingLeft: 4 }}>
              {[
                "Cette réparation est-elle vraiment nécessaire ?",
                "Les pièces proposées sont-elles les bonnes ?",
                "Sont-elles neuves ou déjà utilisées ?",
                "Le prix demandé est-il juste ?",
                "Les pièces seront-elles réellement remplacées ?",
                "Le problème sera-t-il définitivement résolu ?",
              ].map((q) => (
                <div key={q} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <span style={{ color: AC, fontSize: 18, lineHeight: 1.5, flexShrink: 0 }}>?</span>
                  <span style={{ fontSize: "clamp(15px,1.5vw,18px)", color: "#C8C8C8", lineHeight: 1.6 }}>{q}</span>
                </div>
              ))}
            </div>
            <P>La plupart du temps, vous n'avez qu'une seule option&nbsp;: <Strong>faire confiance.</Strong></P>
            <P>Et malheureusement, dans ce secteur, la confiance est souvent mise à rude épreuve.</P>
          </div>
        </div>
      </section>

      {/* MY STORY */}
      <section style={{ padding: "84px 0", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={wrap}>
          <Eyebrow>Mon histoire</Eyebrow>
          <H2>Je m'appelle Wael El Hajj.</H2>
          <div style={block}>
            <P>Entrepreneur basé à <Strong>Abidjan</Strong>, une ville et un pays que j'aime profondément. Depuis plus de <Strong>12 ans</Strong>, j'évolue dans le secteur automobile en travaillant avec des ateliers mécaniques, des gestionnaires de flottes, des fournisseurs de pièces détachées et des propriétaires de véhicules.</P>
            <P>Au fil des années, j'ai vu les mêmes difficultés se répéter encore et encore&nbsp;:</P>
            <div style={{ display: "flex", flexDirection: "column", gap: 14, margin: "6px 0" }}>
              {[
                "Des personnes qui achètent une voiture d'occasion et découvrent quelques semaines plus tard des réparations coûteuses qu'elles n'avaient jamais anticipées.",
                "Des familles qui investissent des sommes importantes dans l'entretien de leur véhicule sans réellement comprendre ce qui a été changé.",
                "Des entreprises qui possèdent plusieurs voitures mais sont incapables de suivre leurs dépenses ou l'historique de maintenance de leur flotte.",
                "Des conducteurs qui choisissent un garage uniquement sur recommandation, puis repartent malgré tout avec un sentiment de doute.",
              ].map((t) => (
                <div key={t} style={{ display: "flex", gap: 14, alignItems: "flex-start", borderLeft: "2px solid rgba(180,255,58,.25)", paddingLeft: 16 }}>
                  <span style={{ fontSize: "clamp(15px,1.5vw,18px)", color: "#9A9A9A", lineHeight: 1.7 }}>{t}</span>
                </div>
              ))}
            </div>
            <P>Afin de mieux comprendre cette réalité, j'ai échangé en face à face avec <Strong>plus de 1 000 propriétaires de véhicules.</Strong> Et une conclusion est devenue évidente&nbsp;:</P>
          </div>
          <div style={quote}>
            <p className="bc" style={quoteText}>
              « Le véritable problème n'est pas la mécanique. Le véritable problème est le manque de transparence. »
            </p>
          </div>
        </div>
      </section>

      {/* WHY I CREATED 1PARC */}
      <section style={{ padding: "84px 0", background: "#040404", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={wrap}>
          <Eyebrow>La solution</Eyebrow>
          <H2>C'est pour cette raison que j'ai créé 1PARC.</H2>
          <div style={block}>
            <P>1PARC est né avec une ambition simple&nbsp;: <Strong>redonner au propriétaire du véhicule le contrôle sur son entretien.</Strong></P>
            <P>La plateforme connecte les propriétaires, les ateliers de réparation, les fournisseurs de pièces et les gestionnaires de flotte dans un même écosystème numérique où chaque intervention est enregistrée, suivie et documentée.</P>
            <P>Grâce à 1PARC, vous savez exactement&nbsp;:</P>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12, marginTop: 20 }}>
            {[
              "Quel atelier est intervenu sur votre véhicule.",
              "Quelles pièces ont été installées.",
              "Quelle marque et quelle référence ont été utilisées.",
              "Combien vous avez payé.",
              "À quelle date les travaux ont été réalisés.",
              "L'historique complet de maintenance de votre voiture.",
            ].map((t) => (
              <div key={t} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#111", border: "1px solid rgba(255,255,255,.07)", borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(180,255,58,.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={AC} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <span style={{ fontSize: 14, color: "#C8C8C8", lineHeight: 1.55 }}>{t}</span>
              </div>
            ))}
          </div>
          <div style={{ ...block, marginTop: 28 }}>
            <P>Vous n'avez plus besoin de vous fier uniquement à votre mémoire ou à votre intuition. Vous disposez enfin de <Strong>preuves.</Strong></P>
          </div>
        </div>
      </section>

      {/* CAR HISTORY VALUE */}
      <section style={{ padding: "84px 0", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={wrap}>
          <Eyebrow>Un historique qui a de la valeur</Eyebrow>
          <H2>Votre voiture mérite un historique clair.</H2>
          <div style={block}>
            <P>Chaque intervention réalisée via 1PARC devient une partie intégrante de l'identité de votre véhicule. Cet historique numérique vous protège aujourd'hui… mais aussi demain.</P>
            <P>Le jour où vous déciderez de vendre votre voiture, l'acheteur n'aura plus besoin de croire uniquement votre parole. Il pourra consulter un historique d'entretien clair, documenté et vérifiable.</P>
            <P>Une voiture bien entretenue a de la valeur. <Strong>Une voiture dont l'entretien est prouvé et traçable en a encore davantage.</Strong></P>
          </div>
        </div>
      </section>

      {/* MORE THAN AN APP */}
      <section style={{ padding: "84px 0", background: "#040404", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={wrap}>
          <Eyebrow>Notre ambition</Eyebrow>
          <H2>Bien plus qu'une application.</H2>
          <div style={block}>
            <P>1PARC n'est pas simplement une plateforme de gestion d'entretien. Notre ambition est de construire <Strong>l'infrastructure numérique de référence pour la maintenance automobile et la gestion de flotte en Afrique.</Strong></P>
            <P>Un environnement où chaque réparation est documentée, chaque décision est transparente et chaque propriétaire peut gérer son véhicule en toute sérénité.</P>
            <P>Parce que vous avez travaillé dur pour acheter votre voiture. <Strong>Vous méritez de savoir exactement comment elle est entretenue, où va votre argent et qui intervient sur votre véhicule.</Strong></P>
          </div>
          <div style={quote}>
            <p className="bc" style={quoteText}>
              « Chez 1PARC, la confiance ne doit jamais être une supposition. Elle doit être démontrée. »
            </p>
          </div>
          {/* signature */}
          <div style={{ marginTop: 40, paddingTop: 26, borderTop: "1px solid rgba(255,255,255,.07)", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(180,255,58,.1)", border: "1px solid rgba(180,255,58,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Logo style={{ height: 18 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#F2F2F2" }}>Wael El Hajj</div>
              <div style={{ fontSize: 12, color: "#555" }}>Fondateur de 1PARC · Abidjan, Côte d'Ivoire</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: AC }}>
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "80px 32px", textAlign: "center" }}>
          <h2 className="bc" style={{ fontSize: "clamp(40px,6vw,72px)", fontWeight: 900, color: "#000", textTransform: "uppercase", letterSpacing: "-2px", lineHeight: .92, marginBottom: 18 }}>
            Reprenez le contrôle.
          </h2>
          <p style={{ fontSize: 17, color: "#333", lineHeight: 1.7, marginBottom: 32, maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
            Créez votre compte, enregistrez vos véhicules, et ne vous fiez plus jamais à une supposition.
          </p>
          <button onClick={() => navigate("/signup")} style={{ padding: "16px 34px", borderRadius: 12, fontSize: 16, background: "#050505", color: AC, border: "none", cursor: "pointer", fontFamily: "'Manrope',sans-serif", fontWeight: 700 }}>
            Je crée mon compte →
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: "40px 0", background: "#030303", textAlign: "center" }}>
        <span style={{ fontSize: 11, color: "#1A1A1A", fontWeight: 700, letterSpacing: ".05em" }}>© {new Date().getFullYear()} 1PARC. TOUS DROITS RÉSERVÉS.</span>
      </footer>
    </div>
  );
};

export default About;
